import React, { useState, useEffect } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    FormGroup,
    Label,
    Input,
    Button,
    Spinner,
    Table,
    Badge,
    Navbar,
    NavbarBrand,
    Nav,
    NavItem
} from 'reactstrap';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { apiURL1 } from '../../config';

const PublicResult = () => {
    const [registrationNumber, setRegistrationNumber] = useState('');
    const [classId, setClassId] = useState('');
    const [examType, setExamType] = useState('');
    const [year, setYear] = useState(dayjs().year());
    const [classes, setClasses] = useState([]);
    const [examTypes, setExamTypes] = useState([]);
    const [schoolInfo, setSchoolInfo] = useState({
        schoolName: "Ummu Dhardha Ladies Islamic college",
        schoolAddress: "Meera Nagar, Beach Road, Akkaraipattu. EP. 32400. Sri Lanka.",
        schoolPhoneNumber: "+94 67 205 3211",
        schoolEmail: "info@ummudhardha.lk"
    });
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const baseURL = apiURL1;
    const logoUrl = "https://res.cloudinary.com/djg7v9dna/image/upload/v1739198257/Logo/kgamz0yjfr9uy8dsbnur.webp";

    useEffect(() => {
        fetchClasses();
        fetchExamTypes();
        fetchSchoolInfo();
    }, []);

    const fetchClasses = async () => {
        try {
            const response = await axios.get(`${baseURL}/publicRoute/allClass`);
            setClasses(response.data);
        } catch (error) {
            // console.error('Error fetching classes:', error);
            toast.error('Error fetching classes');
        }
    };

    const fetchExamTypes = async () => {
        try {
            const response = await axios.get(`${baseURL}/publicRoute/examType`);
            setExamTypes(response.data.data);
        } catch (error) {
            // console.error('Error fetching exam types:', error);
            toast.error('Error fetching exam types');
        }
    };

    const fetchSchoolInfo = async () => {
        try {
            const response = await axios.get(`${baseURL}/publicRoute/schoolInfo`);
            // console.log('response.data is ', response.data);

            // Ensure it's an array and has at least one element
            if (Array.isArray(response.data) && response.data.length > 0) {
                setSchoolInfo(response.data[0]); // Set the first element as schoolInfo
            } else {
                // console.error("Unexpected data format:", response.data);
            }
        } catch (error) {
            // console.error('Error fetching school info:', error);
            // Handle error gracefully (fallback values are already in initial state)
        }
    };


    const handleViewResult = async () => {
        if (!registrationNumber || !classId || !examType || !year) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            setLoading(true);
            const response = await axios.get(
                `${baseURL}/publicRoute/viewResult?registrationNumber=${registrationNumber}&classId=${classId}&type=${examType}&year=${year}`
            );

            if (response.data.success) {
                setResult(response.data.data);
                toast.success('Result fetched successfully');
            } else {
                setResult(null);
                handleResultError(response.data.message, response.status);
            }
        } catch (error) {
            // console.error('Error fetching result:', error.message);
            setResult(null);

            if (error.response) {
                // console.log('Here in error.response ', error.response)
                const { status, data } = error.response;
                // console.log('status, data ', status, data)

                if (status === 403 && data.message === "Result is awaiting") {
                    toast.info("Result is awaiting. Please check again later.");
                    return;
                }
                else {
                    handleResultError(data.message, status);

                }
            }

        } finally {
            setLoading(false);
        }
    };

    // Function to handle different error messages from API
    const handleResultError = (message, status) => {
        if (message.includes("All Fields are required")) {
            toast.error("Please fill in all required fields.");
        } else if (message.includes("Student not found in the specified class")) {
            toast.error("No student found with this registration number in the selected class.");
        } else if (message.includes("No exams found for the given criteria")) {
            toast.error("No exams found for the selected year and exam type. So No Result Found.");
        } else if (message.includes("No results found for the student")) {
            toast.error("No results available.");
        } else if (status === 403 && message === "Result is awaiting") {
            setResult({ status: "awaiting" });
            toast.info("No results available.");
        } else {
            toast.error("Unexpected error occurred. Please try again later.");
        }
    };


    const getGradeColor = (grade) => {
        if (!grade) return 'secondary';
        const gradeUpper = grade.toUpperCase();
        if (gradeUpper === 'A' || gradeUpper === 'A+') return 'success';
        if (gradeUpper === 'B' || gradeUpper === 'B+') return 'info';
        if (gradeUpper === 'C' || gradeUpper === 'C+') return 'primary';
        if (gradeUpper === 'D' || gradeUpper === 'D+') return 'warning';
        if (gradeUpper === 'F') return 'danger';
        return 'secondary';
    };

    const getOverallColor = (percentage) => {
        if (percentage >= 90) return '#28a745';
        if (percentage >= 80) return '#17a2b8';
        if (percentage >= 70) return '#007bff';
        if (percentage >= 60) return '#ffc107';
        if (percentage >= 50) return '#fd7e14';
        return '#dc3545';
    };

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const handlePrint = () => {
        const printContent = `
        <html>
            <head>
                <title>Result - ${result.fullName}</title>
                <style>
                    body { 
                        font-family: Arial, sans-serif; 
                        margin: 0;
                        padding: 20px;
                    }
                    .header { 
                        text-align: center; 
                        margin-bottom: 30px; 
                        border-bottom: 2px solid #003366;
                        padding-bottom: 15px;
                    }
                    .logo {
                        width: 100px;
                        height: auto;
                        margin-bottom: 10px;
                    }
                    .school-name {
                        color: #003366;
                        margin: 5px 0;
                        font-size: 24px;
                    }
                    .school-info { 
                        margin-bottom: 10px;
                        font-size: 14px;
                        color: #555;
                    }
                    .result-title {
                        margin-top: 20px;
                        color: #003366;
                        border-bottom: 1px solid #ddd;
                        padding-bottom: 5px;
                    }
                    .student-info {
                        margin: 20px 0;
                        padding: 15px;
                        border: 1px solid #ddd;
                        border-radius: 5px;
                        background-color: #f9f9f9;
                    }
                    .result-table { 
                        width: 100%; 
                        border-collapse: collapse; 
                        margin: 20px 0; 
                    }
                    .result-table th, .result-table td { 
                        border: 1px solid #ddd; 
                        padding: 10px; 
                        text-align: left; 
                    }
                    .result-table th { 
                        background-color: #003366; 
                        color: white;
                    }
                    .result-table tr:nth-child(even) {
                        background-color: #f2f2f2;
                    }
                    .summary {
                        margin-top: 20px;
                        padding: 15px;
                        border: 1px solid #ddd;
                        border-radius: 5px;
                        background-color: #f9f9f9;
                    }
                    .footer {
                        margin-top: 40px;
                        text-align: center;
                        font-size: 12px;
                        color: #777;
                        border-top: 1px solid #ddd;
                        padding-top: 10px;
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <img src="https://res.cloudinary.com/djg7v9dna/image/upload/v1742946371/mi0wavjsehps20kdzaet.png" alt="School Logo" class="logo"/>
                    <h2 class="school-name">${schoolInfo.schoolName}</h2>
                    <div class="school-info">
                        <p>${schoolInfo.schoolAddress}</p>
                        <p>Tel: ${schoolInfo.schoolPhoneNumber} | Email: ${schoolInfo.schoolEmail}</p>
                    </div>
                    <h1 class="result-title">Examination Result</h1>
                </div>
                <div class="student-info">
                    <p><strong>Student Name:</strong> ${result.fullName || 'N/A'}</p>
                    <p><strong>Registration Number:</strong> ${result?.registrationNumber || 'N/A'}</p>
                    <p><strong>Class:</strong> ${classes.find(c => c._id === result.class)?.className || 'N/A'}</p>
                    <p><strong>Exam Type:</strong> ${examTypes.find(e => e._id === result.examType)?.name || 'N/A'}</p>
                    <p><strong>Academic Year:</strong> ${result?.year || 'N/A'}</p>
                </div>
                <table class="result-table">
                    <thead>
                        <tr>
                            <th>Subject</th>
                            <th>Total Marks</th>
                            <th>Obtained Marks</th>
                            <th>Grade</th>
                            <th>Remarks</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${result.subjects.map(subject => `
                            <tr>
                                <td>${subject?.subjectName || 'N/A'}</td>
                                <td>${subject.totalMarks}</td>
                                <td>${subject.obtainedMarks}</td>
                                <td>${subject?.grade || 'N/A'}</td>
                                <td>${subject?.remarks || '-'}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
                <div class="summary">
                    <p><strong>Total Marks:</strong> ${result?.totalMarks || 'N/A'}</p>
                    <p><strong>Obtained Marks:</strong> ${result?.obtainedMarks || 'N/A'}</p>
                    <p><strong>Overall Percentage:</strong> ${result?.overallPercentage || 'N/A'}%</p>
                    <p><strong>Overall Grade:</strong> ${result?.overallGrade || 'N/A'}</p>
                </div>
                <div class="footer">
                    <p>This is an official result document from ${schoolInfo?.schoolName || 'N/A'}.</p>
                    <p>Date Printed: ${formatDate(new Date())}</p>
                </div>
            </body>
        </html>
        `;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
        setTimeout(() => {
            printWindow.print();
        }, 500);
    };

    const handleDownloadPdf = async () => {
        const response = await axios.get(`${baseURL}/publicRoute/schoolInfo`);
        // console.log('response is ', response.data);

        const pdf = new jsPDF();

        // School info
        const schoolName = response.data.schoolName || "Ummu Dhardha Ladies Islamic College";
        const schoolAddress = response.data.schoolAddress || "Meera Nagar, Beach Road, Akkaraipattu. EP. 32400. Sri Lanka";
        const schoolPhone = response.data.schoolPhoneNumber || "+ 94 67 205 3211";
        const schoolEmail = response.data.schoolEmail || "info@ummudhardha.lk";

        // Student details
        const studentName = result.fullName || "Unknown Student";
        const registrationNumber = result.registrationNumber || "N/A";
        const className = classes.find(c => c._id === result.class)?.className || "N/A";
        const examType = examTypes.find(e => e._id === result.examType)?.name || "N/A";
        const academicYear = result.year || "N/A";

        // Logo URL
        const logoUrl = "https://res.cloudinary.com/djg7v9dna/image/upload/v1742946371/mi0wavjsehps20kdzaet.png";

        try {
            // Fetch image and convert to Base64
            const imgResponse = await axios.get(logoUrl, { responseType: 'arraybuffer' });
            const base64Logo = `data:image/webp;base64,${btoa(
                new Uint8Array(imgResponse.data).reduce((data, byte) => data + String.fromCharCode(byte), '')
            )}`;

            // Add logo to PDF
            pdf.addImage(base64Logo, 'PNG', 80, 10, 30, 30); // Make width and height equal

        } catch (error) {
            // console.error("Error loading the logo:", error);
        }

        // Add school name
        pdf.setFontSize(20);
        pdf.setTextColor(0, 51, 102);
        pdf.text(schoolName, 105, 45, { align: 'center' });

        // School info
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        pdf.text(schoolAddress, 105, 58, { align: 'center' });
        pdf.text(`Tel: ${schoolPhone} | Email: ${schoolEmail}`, 105, 53, { align: 'center' });

        // Title
        pdf.setFontSize(16);
        pdf.setTextColor(0, 51, 102);
        pdf.text("EXAMINATION RESULT", 105, 75, { align: 'center' });

        // Student details
        pdf.setFontSize(12);
        pdf.setTextColor(0, 0, 0);
        pdf.text(`Student Name: ${studentName}`, 20, 80);
        pdf.text(`Registration Number: ${registrationNumber}`, 20, 87);
        pdf.text(`Class: ${className}`, 20, 94);
        pdf.text(`Exam Type: ${examType}`, 20, 101);
        pdf.text(`Academic Year: ${academicYear}`, 20, 108);

        // Subject results table
        const tableColumn = ["Subject", "Total Marks", "Obtained Marks", "Grade", "Remarks"];
        const tableRows = [];

        if (result.subjects && Array.isArray(result.subjects)) {
            result.subjects.forEach(subject => {
                tableRows.push([
                    subject?.subjectName || "N/A",
                    subject?.totalMarks || "N/A",
                    subject?.obtainedMarks || "N/A",
                    subject?.grade || "N/A",
                    subject?.remarks || "N/A"
                ]);
            });
        }

        pdf.autoTable({
            startY: 115,
            head: [tableColumn],
            body: tableRows,
            theme: 'grid',
            headStyles: { fillColor: [0, 51, 102], textColor: 255 },
            alternateRowStyles: { fillColor: [240, 240, 240] }
        });

        // Summary
        const finalY = pdf.lastAutoTable.finalY + 10;
        pdf.text(`Total Marks: ${result.totalMarks || "N/A"}`, 20, finalY);
        pdf.text(`Obtained Marks: ${result.obtainedMarks || "N/A"}`, 20, finalY + 7);
        pdf.text(`Overall Percentage: ${result.overallPercentage || "N/A"}%`, 20, finalY + 14);
        pdf.text(`Overall Grade: ${result.overallGrade || "N/A"}`, 20, finalY + 21);

        // Footer
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`This is an official result document from ${schoolName}.`, 105, finalY + 35, { align: 'center' });
        pdf.text(`Date Printed: ${new Date().toLocaleDateString()}`, 105, finalY + 40, { align: 'center' });

        // Save the PDF
        pdf.save(`result-${studentName.replace(/\s+/g, '-')}-${registrationNumber}.pdf`);
    };


    return (
        <div className="bg-light min-vh-100">
            {/* Header with Logo and School Info */}
            <Navbar color="dark" dark expand="md" className="mb-4 py-3">
                <Container>
                    <NavbarBrand href="/" className="d-flex align-items-center text-white">
                        <img src={logoUrl} alt="School Logo" style={{ height: '50px', marginRight: '15px' }} />
                        <div>
                            <h4 className="m-0 text-white">{schoolInfo.schoolName}</h4>
                            <small className="text-light">Student Result Portal</small>
                        </div>
                    </NavbarBrand>
                    <Nav className="ml-auto" navbar>
                        <NavItem>
                            <div className="text-white text-right">
                                <small className="d-block">{schoolInfo.schoolPhoneNumber}</small>
                                <small>{schoolInfo.schoolEmail}</small>
                            </div>
                        </NavItem>
                    </Nav>
                </Container>
            </Navbar>


            <Container className="py-4">
                <Row>
                    <Col lg="5" className="mb-4">
                        <Card className="shadow border-0 h-100">
                            <CardHeader className="bg-primary text-white border-0">
                                <h4 className="mb-0">Enter Your Details</h4>
                            </CardHeader>
                            <CardBody className="bg-white">
                                <p className="text-muted mb-4">Please provide your registration details to view your examination results.</p>

                                <FormGroup>
                                    <Label className="font-weight-bold">Registration Number *</Label>
                                    <Input
                                        type="text"
                                        placeholder="Enter your registration number"
                                        value={registrationNumber}
                                        onChange={(e) => setRegistrationNumber(e.target.value)}
                                        className="form-control-lg"
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Label className="font-weight-bold">Class *</Label>
                                    <Input
                                        type="select"
                                        value={classId}
                                        onChange={(e) => setClassId(e.target.value)}
                                        className="form-control-lg"
                                    >
                                        <option value="">Select Class</option>
                                        {classes.map((c) => (
                                            <option key={c._id} value={c._id}>
                                                {c.className}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>

                                <FormGroup>
                                    <Label className="font-weight-bold">Exam Type *</Label>
                                    <Input
                                        type="select"
                                        value={examType}
                                        onChange={(e) => setExamType(e.target.value)}
                                        className="form-control-lg"
                                    >
                                        <option value="">Select Exam Type</option>
                                        {examTypes.map((e) => (
                                            <option key={e._id} value={e._id}>
                                                {e.name}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>

                                <FormGroup>
                                    <Label className="font-weight-bold">Academic Year</Label>
                                    <Input
                                        type="select"
                                        value={year}
                                        onChange={(e) => setYear(e.target.value)}
                                        className="form-control-lg"
                                    >
                                        {Array.from({ length: 11 }, (_, i) => dayjs().year() - 5 + i).map((y) => (
                                            <option key={y} value={y}>
                                                {y}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>

                                <div className="d-grid gap-2 mt-4">
                                    <Button
                                        color="primary"
                                        size="lg"
                                        block
                                        onClick={handleViewResult}
                                        disabled={loading}
                                        className="py-3"
                                    >
                                        {loading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Fetching Results...
                                            </>
                                        ) : (
                                            'View Result'
                                        )}
                                    </Button>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg="7">
                        {result ? (
                            <Card className="shadow border-0">
                                <CardHeader className="bg-primary text-white d-flex justify-content-between align-items-center">
                                    <h4 className="mb-0">Result Card</h4>
                                    <div>
                                        <Button color="light" size="sm" onClick={handlePrint} className="me-2">
                                            <i className="fa fa-print me-1"></i> Print
                                        </Button>
                                        <Button color="light" size="sm" onClick={handleDownloadPdf}>
                                            <i className="fa fa-download me-1"></i> Download PDF
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardBody className="bg-white">
                                    <div className="text-center mb-4">
                                        <img src={logoUrl} alt="School Logo" style={{ height: '70px' }} />
                                        <h4 className="mt-2 mb-0" style={{ color: '#003366' }}>{schoolInfo.schoolName}</h4>
                                        <small className="text-muted d-block">{schoolInfo.schoolAddress}</small>
                                        <div className="mt-3 border-top border-bottom py-2">
                                            <h5>Examination Result</h5>
                                        </div>
                                    </div>

                                    <div className="bg-light p-3 rounded mb-4">
                                        <Row>
                                            <Col md="6">
                                                <p className="mb-1"><strong>Student Name:</strong> {result.fullName}</p>
                                                <p className="mb-1"><strong>Registration Number:</strong> {result.registrationNumber}</p>
                                                <p className="mb-1"><strong>Class:</strong> {classes.find(c => c._id === result.class)?.className || 'N/A'}</p>
                                            </Col>
                                            <Col md="6">
                                                <p className="mb-1"><strong>Exam Type:</strong> {examTypes.find(e => e._id === result.examType)?.name || 'N/A'}</p>
                                                <p className="mb-1"><strong>Academic Year:</strong> {result.year}</p>
                                                <p className="mb-1"><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
                                            </Col>
                                        </Row>
                                    </div>

                                    <div className="table-responsive">
                                        <Table bordered hover className="shadow-sm">
                                            <thead className="bg-primary text-white">
                                                <tr>
                                                    <th>Subject</th>
                                                    <th className="text-center">Total Marks</th>
                                                    <th className="text-center">Obtained Marks</th>
                                                    <th className="text-center">Grade</th>
                                                    <th>Remarks</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {result.subjects.map((subject, index) => (
                                                    <tr key={`${subject?.subjectCode || index}`}>
                                                        <td>{subject?.subjectName || 'N/A'}</td>
                                                        <td className="text-center">{subject?.totalMarks || 'N/A'}</td>
                                                        <td className="text-center">{subject?.obtainedMarks || 'N/A'}</td>
                                                        <td className="text-center">
                                                            <Badge color={getGradeColor(subject?.grade || 'N/A')} pill>
                                                                {subject?.grade || 'N/A'}
                                                            </Badge>
                                                        </td>
                                                        <td>{subject?.remarks || 'N/A'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>

                                    <div className="mt-4 p-3 border rounded shadow-sm">
                                        <Row>
                                            <Col md="8">
                                                <Row>
                                                    <Col sm="6" className="mb-2">
                                                        <p className="mb-0 font-weight-bold">Total Marks:</p>
                                                        <h5>{result?.totalMarks || 'N/A'}</h5>
                                                    </Col>
                                                    <Col sm="6" className="mb-2">
                                                        <p className="mb-0 font-weight-bold">Obtained Marks:</p>
                                                        <h5>{result?.obtainedMarks || 'N/A'}</h5>
                                                    </Col>
                                                    <Col sm="6">
                                                        <p className="mb-0 font-weight-bold">Overall Percentage:</p>
                                                        <h5>{result?.overallPercentage || 'N/A'}%</h5>
                                                    </Col>
                                                    <Col sm="6">
                                                        <p className="mb-0 font-weight-bold">Overall Grade:</p>
                                                        <h5>
                                                            <Badge color={getGradeColor(result.overallGrade)} pill className="px-3 py-2">
                                                                {result.overallGrade}
                                                            </Badge>
                                                        </h5>
                                                    </Col>
                                                </Row>
                                            </Col>
                                            <Col md="4" className="d-flex align-items-center justify-content-center">
                                                <div
                                                    className="rounded-circle d-flex align-items-center justify-content-center"
                                                    style={{
                                                        width: '120px',
                                                        height: '120px',
                                                        backgroundColor: getOverallColor(result.overallPercentage),
                                                        color: 'white'
                                                    }}
                                                >
                                                    <div className="text-center">
                                                        <h2 className="mb-0">{result.overallPercentage}%</h2>
                                                        <small>Score</small>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                </CardBody>
                            </Card>
                        ) : (
                            <Card className="shadow border-0 bg-white h-100 d-flex align-items-center justify-content-center p-5">
                                <div className="text-center">
                                    <img
                                        src={logoUrl}
                                        alt="School Logo"
                                        style={{ height: '80px', opacity: 0.5 }}
                                        className="mb-4"
                                    />
                                    <h3 className="text-muted">Your Result Will Appear Here</h3>
                                    <p className="text-muted">
                                        Please enter your registration number, class, and exam type to view your results.
                                    </p>
                                </div>
                            </Card>
                        )}
                    </Col>
                </Row>

                <footer className="mt-5 text-center text-muted">
                    <small>&copy; {new Date().getFullYear()} {schoolInfo.schoolName}. All rights reserved.</small>
                    <div className="mt-1">
                        <small>{schoolInfo.schoolAddress} | {schoolInfo.schoolPhoneNumber} | {schoolInfo.schoolEmail}</small>
                    </div>
                </footer>
            </Container>
        </div>
    );
};

export default PublicResult;